/**
 * Condition Editor Component
 * 
 * Edits a single targeting condition (not logical groups)
 */

import { useCallback } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Select,
  TextField,
  Checkbox,
  Button,
  Text,
  Banner,
} from "@shopify/polaris";
import { DeleteIcon } from "@shopify/polaris-icons";
import type { Condition, ValidationError } from "./types";
import { CONDITION_TYPES, getConditionMetadata } from "./types";

interface ConditionEditorProps {
  condition: Condition;
  onChange: (condition: Condition) => void;
  onDelete: () => void;
  errors?: ValidationError[];
  path: string;
}

export function ConditionEditor({
  condition,
  onChange,
  onDelete,
  errors = [],
  path,
}: ConditionEditorProps) {
  // Filter out logical operators (they're handled by GroupEditor)
  const simpleConditionTypes = CONDITION_TYPES.filter(
    (c) => c.type !== "and" && c.type !== "or" && c.type !== "not"
  );

  const metadata = getConditionMetadata(condition.type);

  // Get errors for this condition
  const conditionErrors = errors.filter((e) => e.path.startsWith(path));

  /**
   * Handle condition type change
   */
  const handleTypeChange = useCallback(
    (newType: string) => {
      const newMetadata = getConditionMetadata(newType);
      if (!newMetadata) return;

      // Create a new condition with defaults based on type
      let newCondition: Condition;

      if (newType === "customer_is_new") {
        newCondition = {
          type: "customer_is_new",
          value: true,
        };
      } else if (newType === "cart_contains_product") {
        newCondition = {
          type: "cart_contains_product",
          productId: "",
        };
      } else if (newType === "cart_contains_collection") {
        newCondition = {
          type: "cart_contains_collection",
          collectionId: "",
        };
      } else if (newType === "geo_region") {
        newCondition = {
          type: "geo_region",
          operator: "equals",
          countryCode: "",
          value: "",
        };
      } else {
        // Standard condition with operator and value
        newCondition = {
          type: newType,
          operator: newMetadata.operators[0]?.value || "equals",
          value: newMetadata.valueType === "number" ? 0 : "",
        } as Condition;
      }

      onChange(newCondition);
    },
    [onChange]
  );

  /**
   * Handle operator change
   */
  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      onChange({
        ...condition,
        operator: newOperator,
      } as Condition);
    },
    [condition, onChange]
  );

  /**
   * Handle value change
   */
  const handleValueChange = useCallback(
    (newValue: string | number | boolean) => {
      if ("value" in condition) {
        onChange({
          ...condition,
          value: newValue,
        } as Condition);
      } else if ("productId" in condition) {
        onChange({
          ...condition,
          productId: newValue as string,
        } as Condition);
      } else if ("collectionId" in condition) {
        onChange({
          ...condition,
          collectionId: newValue as string,
        } as Condition);
      }
    },
    [condition, onChange]
  );

  /**
   * Handle geo region country code change
   */
  const handleCountryCodeChange = useCallback(
    (newCountryCode: string) => {
      if ("countryCode" in condition) {
        onChange({
          ...condition,
          countryCode: newCountryCode,
        } as Condition);
      }
    },
    [condition, onChange]
  );

  /**
   * Render value input based on condition type
   */
  const renderValueInput = () => {
    if (!metadata) return null;

    if (metadata.valueType === "boolean") {
      return (
        <Checkbox
          label="Is new customer"
          checked={("value" in condition && Boolean(condition.value)) || false}
          onChange={handleValueChange}
        />
      );
    }

    if (metadata.valueType === "number") {
      return (
        <TextField
          label="Value"
          type="number"
          value={String(("value" in condition && condition.value) || 0)}
          onChange={(value) => handleValueChange(parseFloat(value) || 0)}
          autoComplete="off"
          error={conditionErrors.find((e) => e.path === `${path}.value`)?.message}
        />
      );
    }

    if (metadata.type === "geo_region") {
      return (
        <BlockStack gap="200">
          <TextField
            label="Country Code (ISO 2-letter, e.g. US, CA)"
            value={("countryCode" in condition && condition.countryCode) || ""}
            onChange={handleCountryCodeChange}
            autoComplete="off"
            error={conditionErrors.find((e) => e.path === `${path}.countryCode`)?.message}
          />
          <TextField
            label="Region/State Code"
            value={("value" in condition && String(condition.value)) || ""}
            onChange={(value) => handleValueChange(value)}
            autoComplete="off"
            error={conditionErrors.find((e) => e.path === `${path}.value`)?.message}
          />
        </BlockStack>
      );
    }

    if (metadata.valueType === "productId") {
      const value = "productId" in condition ? condition.productId : "value" in condition ? String(condition.value) : "";
      return (
        <TextField
          label="Product ID (Shopify gid://)"
          value={value}
          onChange={handleValueChange}
          autoComplete="off"
          error={conditionErrors.find((e) => e.path === `${path}.productId` || e.path === `${path}.value`)?.message}
        />
      );
    }

    if (metadata.valueType === "collectionId") {
      const value = "collectionId" in condition ? condition.collectionId : "";
      return (
        <TextField
          label="Collection ID (Shopify gid://)"
          value={value}
          onChange={handleValueChange}
          autoComplete="off"
          error={conditionErrors.find((e) => e.path === `${path}.collectionId`)?.message}
        />
      );
    }

    if (metadata.valueType === "countryCode") {
      return (
        <TextField
          label="Country Code (ISO 2-letter, e.g. US, CA)"
          value={("value" in condition && String(condition.value)) || ""}
          onChange={(value) => handleValueChange(value.toUpperCase())}
          autoComplete="off"
          error={conditionErrors.find((e) => e.path === `${path}.value`)?.message}
          maxLength={2}
        />
      );
    }

    // Default: string input
    return (
      <TextField
        label="Value"
        value={("value" in condition && String(condition.value)) || ""}
        onChange={handleValueChange}
        autoComplete="off"
        error={conditionErrors.find((e) => e.path === `${path}.value`)?.message}
      />
    );
  };

  return (
    <Card>
      <BlockStack gap="400">
        {conditionErrors.length > 0 && (
          <Banner tone="critical">
            <BlockStack gap="100">
              {conditionErrors.map((error, idx) => (
                <Text key={idx} as="p">
                  {error.message}
                </Text>
              ))}
            </BlockStack>
          </Banner>
        )}

        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingSm">
            Condition
          </Text>
          <Button
            icon={DeleteIcon}
            onClick={onDelete}
            variant="plain"
            tone="critical"
            accessibilityLabel="Delete condition"
          />
        </InlineStack>

        <Select
          label="Condition type"
          options={[
            { label: "— Select condition —", value: "" },
            ...simpleConditionTypes.map((c) => ({
              label: `${c.label} (${c.category})`,
              value: c.type,
            })),
          ]}
          value={condition.type}
          onChange={handleTypeChange}
          error={conditionErrors.find((e) => e.path === `${path}.type`)?.message}
        />

        {metadata && metadata.description && (
          <Text as="p" tone="subdued">
            {metadata.description}
          </Text>
        )}

        {metadata && metadata.operators.length > 0 && (
          <Select
            label="Operator"
            options={metadata.operators.map((op) => ({
              label: op.label,
              value: op.value,
            }))}
            value={("operator" in condition && condition.operator) || ""}
            onChange={handleOperatorChange}
            error={conditionErrors.find((e) => e.path === `${path}.operator`)?.message}
          />
        )}

        {renderValueInput()}
      </BlockStack>
    </Card>
  );
}
